import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  collection,
  addDoc,
  updateDoc,
  getDoc,
  doc,
  serverTimestamp,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../../firebase";
// D-291 (P3b): one GSTIN field for every vendor (gst_number) + an
// "Unregistered vendor" tick. GSTIN is optional, but if typed it must be a
// valid GSTIN (15 characters + check character) - the purchase bill decides
// CGST + SGST vs IGST from it.
import { validateGstin, vendorGstinOf } from "../../utils/gstBill";

// What to save for the GST fields. gst_registered: true = valid GSTIN,
// false = ticked Unregistered, null = not entered yet (bill screen asks).
// Returns { error } when a typed GSTIN is not valid.
function gstFieldsFrom(form) {
  if (form.gst_unregistered) return { gst_number: "", gst_registered: false };
  if (!form.gst_number.trim()) return { gst_number: "", gst_registered: null };
  const check = validateGstin(form.gst_number);
  if (!check.ok) return { error: check.reason };
  return { gst_number: check.gstin, gst_registered: true };
}

const EMPTY_FORM = {
  vendor_name:    "",
  contact_person: "",
  phone:          "",
  email:          "",
  gst_number:     "",
  gst_unregistered: false, // screen-only tick; saved as gst_registered: false
  address:        "",
  city:           "",
  state:          "",
  pincode:        "",
  type:           "material",
  status:         "active",
};

export default function VendorMaster() {
  const [searchParams]    = useSearchParams();
  const navigate          = useNavigate();
  const editId            = searchParams.get("id"); // ?id=xxx → edit mode
  const isEditMode        = Boolean(editId);

  // ── User from localStorage (consistent with rest of app) ──────────────────
  const me = JSON.parse(localStorage.getItem("user") || "{}");

  // ── State ─────────────────────────────────────────────────────────────────
  const [formData,    setFormData]    = useState(EMPTY_FORM);
  const [loading,     setLoading]     = useState(false);
  const [fetching,    setFetching]    = useState(false); // loading existing vendor
  const [fetchError,  setFetchError]  = useState("");

  // ── On edit mode: fetch existing vendor and pre-fill form ─────────────────
  useEffect(() => {
    if (!editId) return;

    const fetchVendor = async () => {
      setFetching(true);
      setFetchError("");
      try {
        const snap = await getDoc(doc(db, "vendors", editId));
        if (!snap.exists()) {
          setFetchError("Vendor not found.");
          return;
        }
        const data = snap.data();

        // Multi-tenant safety — strict check (data fixed in Firebase)
        if (data.company_id && data.company_id !== me.company_id) {
          setFetchError("Access denied.");
          return;
        }

        // Pre-fill form with existing values
        setFormData({
          vendor_name:    data.vendor_name    || "",
          contact_person: data.contact_person || "",
          phone:          data.phone          || "",
          email:          data.email          || "",
          // vendorGstinOf: old Quick Create vendors saved the GSTIN as `gst`
          gst_number:     vendorGstinOf(data),
          gst_unregistered: data.gst_registered === false,
          address:        data.address        || "",
          city:           data.city           || "",
          state:          data.state          || "",
          pincode:        data.pincode        || "",
          type:           data.type           || "material",
          status:         data.status         || "active",
        });
      } catch (err) {
        console.error("[VendorMaster] fetchVendor error:", err);
        setFetchError("Failed to load vendor.");
      } finally {
        setFetching(false);
      }
    };

    fetchVendor();
  }, [editId, me.company_id]);

  // ── Form change handler ───────────────────────────────────────────────────
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ── Generate vendor code (add mode only) ──────────────────────────────────
  const generateVendorCode = async () => {
  const snapshot = await getDocs(query(
    collection(db, "vendors"),
    where("company_id", "==", me.company_id || "")
  ));
  // Max existing code se +1 — deletion se affected nahi hoga
  let max = 0;
  snapshot.docs.forEach(d => {
    const code = d.data().vendor_code || "";
    const num = parseInt(code.replace("VEN-", "")) || 0;
    if (num > max) max = num;
  });
  return `VEN-${String(max + 1).padStart(3, "0")}`;
};

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.vendor_name.trim()) { alert("Vendor Name is required"); return; }
    if (!formData.phone.trim())       { alert("Phone is required"); return; }
    const gstFields = gstFieldsFrom(formData);
    if (gstFields.error) { alert(gstFields.error); return; }

    try {
      setLoading(true);

      // Edit mode phone duplicate check
const phoneCheckQuery = query(
  collection(db, "vendors"),
  where("company_id", "==", me.company_id || ""),
  where("phone", "==", formData.phone.trim())
);
const phoneCheckSnap = await getDocs(phoneCheckQuery);
if (!phoneCheckSnap.empty && phoneCheckSnap.docs[0].id !== editId) {
  const existing = phoneCheckSnap.docs[0].data();
  alert(`Phone already exists: ${existing.vendor_name} (${existing.vendor_code})`);
  setLoading(false);
  return;
}

      if (isEditMode) {
        // ── EDIT MODE: updateDoc ─────────────────────────────────────────
        await updateDoc(doc(db, "vendors", editId), {
          vendor_name:    formData.vendor_name.trim(),
          contact_person: formData.contact_person.trim(),
          phone:          formData.phone.trim(),
          email:          formData.email.trim(),
          gst_number:     gstFields.gst_number,
          gst_registered: gstFields.gst_registered,
          address:        formData.address.trim(),
          city:           formData.city.trim(),
          state:          formData.state.trim(),
          pincode:        formData.pincode.trim(),
          type:           formData.type,
          status:         formData.status,
          updated_at:     serverTimestamp(),
          updated_by:     me.uid || me.id || "",
          updated_by_name: me.name || "",
        });

        alert("Vendor updated successfully");
        navigate("/vendor-list");

      } else {
        // ── ADD MODE: duplicate check + addDoc ───────────────────────────
        // ✅ FIX: company_id filter on duplicate check (was missing → cross-tenant collision)
        // Phone duplicate check — hard block
const phoneQuery = query(
  collection(db, "vendors"),
  where("company_id", "==", me.company_id || ""),
  where("phone", "==", formData.phone.trim())
);
const phoneSnap = await getDocs(phoneQuery);
if (!phoneSnap.empty) {
  const existing = phoneSnap.docs[0].data();
  alert(`Phone already exists: ${existing.vendor_name} (${existing.vendor_code})`);
  setLoading(false);
  return;
}

// Name duplicate check — hard block
const nameQuery = query(
  collection(db, "vendors"),
  where("company_id", "==", me.company_id || ""),
  where("vendor_name", "==", formData.vendor_name.trim())
);
const nameSnap = await getDocs(nameQuery);
if (!nameSnap.empty) {
  alert("Vendor with this name already exists");
  setLoading(false);
  return;
}

        const vendor_code = await generateVendorCode();

        // gst_unregistered is a screen-only field - never saved as it is
        const { gst_unregistered, ...formFields } = formData;

        await addDoc(collection(db, "vendors"), {
          ...formFields,
          gst_number:     gstFields.gst_number,
          gst_registered: gstFields.gst_registered,
          vendor_name:    formData.vendor_name.trim(),
          vendor_code,
          // ✅ FIX: use localStorage company_id (not auth.currentUser.uid)
          company_id:     me.company_id || "",
          created_by:     me.uid || me.id || "",
          created_by_name: me.name || "",
          created_at:     serverTimestamp(),
          updated_at:     serverTimestamp(),
        });

        alert("Vendor added successfully");

        // Reset form
        setFormData(EMPTY_FORM);
      }

    } catch (error) {
      console.error("[VendorMaster] submit error:", error);
      alert(isEditMode ? "Failed to update vendor" : "Failed to add vendor");
    } finally {
      setLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (fetching) {
    return (
      <div className="container-fluid py-4 px-4">
        <p className="text-muted">Loading vendor...</p>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="container-fluid py-4 px-4">
        <p className="text-danger">{fetchError}</p>
        <button className="btn btn-secondary mt-2" onClick={() => navigate("/vendor-list")}>
          ← Back to Vendor List
        </button>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4 px-4">
      <div className="card shadow border-0 rounded-4">

        <div className="card-header bg-primary text-white rounded-top-4 d-flex justify-content-between align-items-center">
          <div>
            <h4 className="mb-0">{isEditMode ? "Edit Vendor" : "Vendor Master"}</h4>
            <small>{isEditMode ? "Update vendor details" : "Add and manage vendor details"}</small>
          </div>
          {isEditMode && (
            <button
              className="btn btn-outline-light btn-sm"
              onClick={() => navigate("/vendor-list")}
            >
              ← Back
            </button>
          )}
        </div>

        <div className="card-body bg-light">
          <form onSubmit={handleSubmit} className="row g-3">

            <div className="col-md-4">
              <label className="form-label fw-semibold">Vendor Name *</label>
              <input
                type="text"
                name="vendor_name"
                className="form-control rounded-3"
                value={formData.vendor_name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Contact Person</label>
              <input
                type="text"
                name="contact_person"
                className="form-control rounded-3"
                value={formData.contact_person}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Phone *</label>
              <input
                type="text"
                name="phone"
                className="form-control rounded-3"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Email</label>
              <input
                type="email"
                name="email"
                className="form-control rounded-3"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">GSTIN</label>
              <input
                type="text"
                name="gst_number"
                className="form-control rounded-3"
                placeholder={formData.gst_unregistered ? "Unregistered vendor" : "15 characters, e.g. 09AAQFB2219L1Z5"}
                value={formData.gst_unregistered ? "" : formData.gst_number}
                onChange={e => setFormData({ ...formData, gst_number: e.target.value.toUpperCase() })}
                disabled={formData.gst_unregistered}
                maxLength={15}
              />
              {!formData.gst_unregistered && formData.gst_number.trim() && (() => {
                const check = validateGstin(formData.gst_number);
                return check.ok
                  ? <small className="text-success">Valid GSTIN · state code {check.state_code}</small>
                  : <small className="text-danger">{check.reason}</small>;
              })()}
              <div className="form-check mt-1">
                <input
                  type="checkbox"
                  id="gst_unregistered"
                  className="form-check-input"
                  checked={formData.gst_unregistered}
                  onChange={e => setFormData({ ...formData, gst_unregistered: e.target.checked })}
                />
                <label htmlFor="gst_unregistered" className="form-check-label small">
                  Unregistered vendor (no GSTIN)
                </label>
              </div>
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Address</label>
              <input
                type="text"
                name="address"
                className="form-control rounded-3"
                value={formData.address}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">City</label>
              <input
                type="text"
                name="city"
                className="form-control rounded-3"
                value={formData.city}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">State</label>
              <input
                type="text"
                name="state"
                className="form-control rounded-3"
                value={formData.state}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Pincode</label>
              <input
                type="text"
                name="pincode"
                className="form-control rounded-3"
                value={formData.pincode}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Type</label>
              <select
                name="type"
                className="form-select rounded-3"
                value={formData.type}
                onChange={handleChange}
              >
                <option value="material">Material</option>
                <option value="service">Service</option>
                <option value="both">Both</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Status</label>
              <select
                name="status"
                className="form-select rounded-3"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="col-12 text-end mt-4">
              {isEditMode && (
                <button
                  type="button"
                  className="btn btn-secondary px-4 py-2 rounded-3 me-2"
                  onClick={() => navigate("/vendor-list")}
                  disabled={loading}
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                className="btn btn-primary px-4 py-2 rounded-3"
                disabled={loading}
              >
                {loading
                  ? (isEditMode ? "Updating..." : "Saving...")
                  : (isEditMode ? "Update Vendor" : "Add Vendor")
                }
              </button>
            </div>

          </form>
        </div>

      </div>
    </div>
  );
}